import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Sidenavaction } from '../../../../assets/js/application/sidenavaction';
import * as moment from 'moment';
import { UomService } from '../../../core/services/uom.service';
import UOMMasterModel from '../../../core/models/uom.model';
import { ToastrService } from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/core/services/common-methods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';
import { BatchLoader } from 'src/app/core/utilities/batch-loader.util';

declare var $: any;

/**
 * UomComponent
 *
 * This component is responsible for managing Unit of Measure (UOM) master data within the application.
 * It provides an administrative interface for creating, reading, updating, and listing UOM definitions
 * used throughout the material management system.
 *
 * Key Responsibilities:
 * - Fetch and display a list of all available UOMs.
 * - Provide a search interface to filter UOMs by name.
 * - Implement infinite scrolling for efficient data rendering using BatchLoader.
 * - Provide a form for adding new UOMs with validation.
 * - Provide functionality to edit existing UOM details.
 * - Handle form submission and integration with UomService.
 * - Manage UI state for loading indicators, form visibility, and submission status.
 *
 * User Roles:
 * - System Administrators: Manage master data configurations.
 * - Material Managers: Define units for inventory items.
 *
 * Features:
 * - **CRUD Operations**: Create and Update UOMs.
 * - **Real-time Search**: Filter list by UOM name.
 * - **Infinite Scroll**: Load data in batches to improve performance.
 * - **Form Validation**: Ensures UOM names follow specific patterns and length constraints.
 * - **Date Formatting**: Formats 'updatedAt' timestamps for display.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Fetches all UOMs from `UomService`.
 * 3. Formats dates using `moment`.
 * 4. Initializes `BatchLoader` with the full dataset.
 * 5. User interactions (scroll, search, add/edit) trigger state updates and service calls.
 * 6. `addUpdateUom` sends data to backend and refreshes the list on success.
 *
 * @component
 * @selector app-uom
 * @example
 * <app-uom></app-uom>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-uom',
  templateUrl: './uom.component.html',
  styleUrls: ['./uom.component.scss']
})
export class UnitsOfMeasuresComponent implements OnInit, OnDestroy {
  /**
   * Array holding all fetched UOMs from the backend.
   * Acts as the source of truth for the list before filtering.
   * @type {UOMMasterModel[]}
   * @default []
   */
  public uoms: UOMMasterModel[] = [];

  /**
   * Array holding the currently displayed UOMs.
   * This list is populated by the BatchLoader (subset of uoms or filteredUoms).
   * @type {UOMMasterModel[]}
   * @default []
   */
  public uomsList: UOMMasterModel[] = [];

  /**
   * Model for the current UOM being added or edited.
   * Bound to the form for data manipulation.
   * @type {UOMMasterModel}
   */
  public uom: UOMMasterModel = new UOMMasterModel();

  /**
   * Array holding UOMs that match the current search criteria.
   * Used as the source for BatchLoader when filtering is active.
   * @type {UOMMasterModel[]}
   * @default []
   */
  public filteredUoms: UOMMasterModel[] = [];

  /**
   * Current search query string entered by the user.
   * @type {string}
   */
  public search: string;

  /**
   * Flag indicating if the form is in 'Create' mode.
   * @type {boolean}
   */
  public submitButton: boolean;

  /**
   * Flag indicating if the form is in 'Update' mode.
   * @type {boolean}
   */
  public updateButton: boolean;

  /**
   * Reactive form group for UOM details (name, description).
   * @type {FormGroup}
   */
  public registerForm: FormGroup;

  /**
   * Flag indicating if the form has been submitted.
   * Used to trigger validation error display in the UI.
   * @type {boolean}
   * @default false
   */
  public submitted = false;

  /**
   * Flag to disable the submit button during async operations.
   * @type {boolean}
   */
  public submitButtonDisabled: boolean;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   * @default false
   */
  public enableDisablePageLoader: boolean = false;

  /**
   * Flag to control the visibility of the list loader spinner during infinite scroll.
   * @type {boolean}
   * @default false
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * Tracks the number of items currently loaded by the batch loader.
   * @type {number}
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * Flag indicating if a search filter is currently active.
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * Flag indicating if the list is empty (no data or no search results).
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /**
   * Timeout reference for search input debouncing.
   * @type {any}
   */
  searchTimeout: any;

  /**
   * Count of fields in the form, used for layout adjustments.
   * @type {number}
   * @default 0
   */
  public formFieldCount: number = 0;

  /**
   * Flag to toggle the visibility of the add/edit form.
   * @type {boolean}
   * @default false
   */
  public showForm: boolean = false;

  /**
   * Constructor for UomComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * Form Validation Rules:
   * - name: Required, Min 3, Max 20, Pattern (alphanumeric, spaces, dots).
   * - description: Max 200 characters.
   *
   * @constructor
   * @param {ToastrService} toasterService - Service for displaying toast notifications.
   * @service ToastrService
   * @param {UomService} uomService - Service for UOM API operations.
   * @service UomService
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods and error handling.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(public toasterService: ToastrService, public uomService: UomService, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.uom = new UOMMasterModel();
    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[.a-zA-Z ]*$')]],
      description: ['', [Validators.maxLength(200)]]
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * Utility for handling batch loading of data for infinite scroll.
   * @type {BatchLoader<any>}
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Sets up side navigation.
   * 2. Resets component state and form.
   * 3. Tracks the current route.
   * 4. Fetches all UOMs from the backend.
   * 5. Formats the 'updatedAt' date for each UOM.
   * 6. Initializes the batch loader and loads the first batch of data.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    this.sidenavaction.x_navigation();
    try {
      window.scrollTo(0, 0);
      this.enableDisablePageLoader = true;
      this.clear();
      this.uoms = [];
      this.uomsList = [];
      this.filteredUoms = [];
      this.isFiltering = false;
      this.search = '';
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      this.uoms = await this.uomService.getAllUom();
      if (this.uoms?.length > 0) {
        this.uoms.forEach(eachUom => {
          eachUom.updatedAt = JSON.parse(JSON.stringify(moment(eachUom.updatedAt).format('DD-MMM-YYYY hh:mm:ss A')));
        });
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.enableDisablePageLoader = false;
    } finally {
      this.enableDisablePageLoader = false;
    }
  }

  /**
   * Host listener for window scroll events.
   * Triggers the check for loading the next batch of data when scrolling near the bottom.
   *
   * @method onScroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredUoms : this.uoms,
      (updatedList) => {
        this.uomsList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests.
   * - Destroys and resets the batch loader.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.uomService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader.reset();
  }

  /**
   * Getter for easy access to form controls in the template.
   *
   * @method uomFormValidation
   * @returns {AbstractControl} Form controls
   */
  get uomFormValidation() {
    return this.registerForm.controls;
  }

  /**
   * Filters the UOM list based on the search query.
   *
   * - Filters by name (case-insensitive).
   * - Updates `filteredUoms`.
   * - Resets the batch loader to display filtered results.
   *
   * @async
   * @method applySearch
   * @returns {Promise<void>}
   * @side-effect Updates `filteredUoms`, `uomsList`, `noList`, `isFiltering`.
   */
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.uoms.filter(uom =>
        uom.name?.toLowerCase().includes(query)
      )
      : this.uoms;
    this.filteredUoms = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.uomsList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  /**
   * Debounces search input changes.
   * Calls `applySearch` after a delay to prevent excessive filtering.
   *
   * @method onSearchChanged
   * @returns {void}
   */
  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  /**
   * Resets the form and component state to default values.
   *
   * @method clear
   * @returns {void}
   */
  public clear() {
    this.registerForm.reset();
    this.uom = new UOMMasterModel();
    this.submitButtonDisabled = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
    this.showForm = false;
  }

  /**
   * Handles the submission of the UOM form (Add or Update).
   *
   * - Validates the form.
   * - Calls `create` or `updateOneByID` service methods based on mode.
   * - Displays success notification.
   * - Refreshes the list via `ngOnInit`.
   *
   * @async
   * @method addUpdateUom
   * @returns {Promise<void>}
   * @throws {Error} If the service call fails.
   */
  public async addUpdateUom() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.registerForm.invalid) return;
    // GET FORM VALUES
    const formData = this.registerForm.value; // contains updated values
    try {
      this.submitButtonDisabled = true;
      if (this.submitButton) {
        await this.uomService.create(formData);
        this.toasterService.success('UOM created successfully', '', {
          timeOut: 2000
        });
      } else {
        const updateUom = { ...formData };
        delete updateUom.id;
        await this.uomService.updateOneByID(this.uom.id, updateUom);
        this.toasterService.success('UOM updated successfully', '', {
          timeOut: 2000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    } finally {
      this.submitButtonDisabled = false;
    }
  }

  /**
   * Retrieves a UOM by ID and populates the form for editing.
   * Sets the component to 'Update' mode.
   *
   * @method getUomById
   * @param {string} id - The ID of the UOM to edit.
   * @returns {void}
   */
  public getUomById(id: string) {
    this.showForm = true;
    this.submitButton = false;
    this.updateButton = true;
    this.showForm = true;
    const foundUom = this.uomsList.find(uom => uom?.id === id);
    this.uom = foundUom ? { ...foundUom } : new UOMMasterModel(); // clone object
    if (this.uom) {
      this.registerForm.patchValue({
        name: this.uom.name,
        description: this.uom.description
      });
    }
  }

  /**
   * Opens the form in 'Create' mode.
   *
   * @method openForm
   * @returns {void}
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}
